// config
import l from 'config/localization'
import constants from 'config/constants'

// Imported constants
const storyTypes = constants.STORY_TYPES

// assets
import ActivityIcon from 'assets/images/svgs/story-good-deed.svg'
import OppIcon from 'assets/images/svgs/icon-org-page-opportunity.svg'
import OrgIcon from 'assets/images/svgs/icon-org-page-organisation.svg'
import StoryFeaturedIcon from 'assets/images/svgs/story-featured.svg'
import StoryUpdateIcon from 'assets/images/svgs/story-update.svg'
import DonationIcon from 'assets/images/cause-type-icons/money_icon_dark.svg'
import GoodsIcon from 'assets/images/cause-type-icons/goods_icon_dark.svg'
import PledgeIcon from 'assets/images/cause-type-icons/pledge_icon_dark.svg'
import VolunteerIcon from 'assets/images/cause-type-icons/volunteer_icon_dark.svg'

const deriveAttribution = ({ storyBeneficiary }) => {
  if (!storyBeneficiary) return ''
  if (!storyBeneficiary.name) return ''

  return {
    beneficiaryName: `${storyBeneficiary.name}`,
    beneficiaryLink: storyBeneficiary.link
  }
}

const noBeneficiaryStoryTypes = [storyTypes.ORG_FEATURE, storyTypes.ORG_UPDATE]

const deriveStoryTypeIcon = ({ story }) => {
  const storyType = story.story_type

  switch (storyType) {
    case storyTypes.GOOD_DEED:
      return ActivityIcon
    case storyTypes.OPP_FEEDBACK:
      return OppIcon
    case storyTypes.OPP_HOST_FEEDBACK:
      return OppIcon
    case storyTypes.CAMP_FEATURE:
      return StoryFeaturedIcon
    case storyTypes.CAMP_UPDATE:
      return StoryUpdateIcon
    case storyTypes.DONATION_TO_ORG:
      return DonationIcon
    case storyTypes.DONATION_TO_ORG_CONFIRMED:
      return DonationIcon
    case storyTypes.OPP_FEATURE:
      return StoryFeaturedIcon
    case storyTypes.OPP_SIGN_UP_PLEDGE:
      return PledgeIcon
    case storyTypes.OPP_SIGN_UP_VOLUNTEER:
      return VolunteerIcon
    case storyTypes.OPP_SIGN_UP_GOODS:
      return GoodsIcon
    case storyTypes.OPP_SIGN_UP_MONEY:
      return DonationIcon
    case storyTypes.OPP_SIGN_UP_MONEY_CORPORATE:
      return DonationIcon
    case storyTypes.OPP_SIGN_UP_MONEY_CONFIRMED:
      return DonationIcon
    case storyTypes.OPP_SIGN_UP_MONEY_CORPORATE_CONFIRMED:
      return DonationIcon
    case storyTypes.OPP_CREATED:
      return OppIcon
    case storyTypes.OPP_UPDATE:
      return StoryUpdateIcon
    case storyTypes.ORG_FEATURE:
      return StoryFeaturedIcon
    case storyTypes.ORG_INVITE:
      return OrgIcon
    case storyTypes.ORG_UPDATE:
      return StoryUpdateIcon
    default:
      return null
  }
}

const deriveGoalTypeIcon = ({ goalType }) => {
  switch (goalType) {
    case constants.OPPORTUNITY_GOAL_TYPES.MONEY:
      return DonationIcon
    case constants.OPPORTUNITY_GOAL_TYPES.VOLUNTEERING:
      return VolunteerIcon
    case constants.OPPORTUNITY_GOAL_TYPES.PLEDGE:
      return PledgeIcon
    case constants.OPPORTUNITY_GOAL_TYPES.GOODS:
      return GoodsIcon
    default:
      return null
  }
}

const deriveAction = ({
  applicationWithdrawn,
  isOppMasonry,
  storyAmount,
  storyOutcomeStatus,
  storyType
}) => {
  const currencyAmount = storyAmount ? storyAmount.toLocaleString('en-US') : null

  const verb =
    storyOutcomeStatus === constants.APPLICATION_STATUS_NAMES.CONFIRMED
      ? 'donated'
      : 'committed to donate'

  switch (storyType) {
    case storyTypes.GOOD_DEED:
      return `did a good deed`
    case storyTypes.OPP_FEEDBACK:
      if (applicationWithdrawn) return 'withdrew'
      return isOppMasonry ? 'shared feedback' : `shared feedback about a ${l('OPP')}`
    case storyTypes.OPP_HOST_FEEDBACK:
      return isOppMasonry ? 'shared host feedback' : `shared host feedback for a ${l('OPP')}`
    case storyTypes.CAMP_FEATURE:
      return `launched a ${l('CAMP')}`
    case storyTypes.CAMP_UPDATE:
      return `posted a ${l('CAMP')} update`
    case storyTypes.DONATION_TO_ORG:
      return storyAmount ? `${verb} R${currencyAmount}` : `${verb} money`
    case storyTypes.DONATION_TO_ORG_CONFIRMED:
      return storyAmount ? `donated R${currencyAmount}` : `donated money`
    case storyTypes.OPP_FEATURE:
      return `launched a ${l('OPP')}`
    case storyTypes.OPP_SIGN_UP_PLEDGE:
      return `made a pledge`
    case storyTypes.OPP_SIGN_UP_VOLUNTEER:
      return isOppMasonry ? 'signed up to volunteer' : `signed up to volunteer for a ${l('OPP')}`
    case storyTypes.OPP_SIGN_UP_GOODS:
      return storyAmount ? `${verb} ${storyAmount} goods` : `${verb} goods`
    case storyTypes.OPP_SIGN_UP_MONEY:
      return storyAmount ? `${verb} R${currencyAmount}` : `${verb} money`
    case storyTypes.OPP_SIGN_UP_MONEY_CORPORATE:
      return storyAmount ? `${verb} R${currencyAmount}` : `${verb} money`
    case storyTypes.OPP_SIGN_UP_MONEY_CONFIRMED:
      return storyAmount ? `donated R${currencyAmount}` : `donated money`
    case storyTypes.OPP_SIGN_UP_MONEY_CORPORATE_CONFIRMED:
      return storyAmount ? `donated R${currencyAmount}` : `donated money`
    case storyTypes.OPP_CREATED:
      return isOppMasonry ? `created this ${l('OPP')}` : `created a ${l('OPP')}`
    case storyTypes.OPP_UPDATE:
      return isOppMasonry ? `updated this ${l('OPP')}` : `updated a ${l('OPP')}`
    case storyTypes.ORG_FEATURE:
      return `posted their philanthropy goals`
    case storyTypes.ORG_INVITE:
      return `grew the Brownie Points network`
    case storyTypes.ORG_UPDATE:
      return `posted an update`
    default:
      return isOppMasonry ? 'signed up' : `signed up to a ${l('OPP')}`
  }
}

const deriveStoryAction = ({
  applicationWithdrawn,
  beneficiaryName,
  isOppMasonry,
  storyAmount,
  storyOutcomeStatus,
  storyType
}) => {
  const action = deriveAction({
    applicationWithdrawn,
    isOppMasonry,
    storyAmount,
    storyOutcomeStatus,
    storyType
  })
  const storyHasBeneficiary = beneficiaryName
  const rightSpaceAction = !!storyHasBeneficiary

  return rightSpaceAction ? `${action} ` : action
}

const deterimeBeneficiaryRendering = ({ beneficiaryName, storyType }) => {
  if (!beneficiaryName || !storyType) return false
  if (noBeneficiaryStoryTypes.includes(storyType)) return false

  return true
}

export {
  deriveAttribution,
  deriveGoalTypeIcon,
  deriveStoryAction,
  deriveStoryTypeIcon,
  deterimeBeneficiaryRendering
}

export default {
  deriveAttribution,
  deriveGoalTypeIcon,
  deriveStoryAction,
  deriveStoryTypeIcon,
  deterimeBeneficiaryRendering
}
